import { ScatterplotLayer } from "@deck.gl/layers";
import axios from "axios";
import { COORDINATE_SYSTEM } from "@deck.gl/core";

// In bulkSignalHandler.js

// Create a cache for raw data at file level
const rawDataCache = new Map();

export const downloadAndParseCSV = async (
  fileLocations,
  user,
  limit = 500000,
  offset = 0,
  timeRange = {}
) => {
  if (!fileLocations || !Array.isArray(fileLocations) || fileLocations.length === 0) {
    console.warn("No file locations provided");
    return { data: [], stats: null, totalCount: 0, hasMore: false };
  }

  const cacheKey = fileLocations.join(',');

  // Check cache first
  if (rawDataCache.has(cacheKey)) {
    console.log("Using cached data for filtering");
    const cachedData = rawDataCache.get(cacheKey);

    // Apply timeRange filtering to cached data
    const filteredData = cachedData.filter(item => {
      const timestamp = parseInt(item.TIMESTAMP);
      const start = timeRange.startDateTime ? new Date(timeRange.startDateTime).getTime() : 0;
      const end = timeRange.endDateTime ? new Date(timeRange.endDateTime).getTime() : Infinity;
      return timestamp >= start && timestamp <= end;
    });

    // Calculate stats for filtered data
    const stats = {
      totalPoints: filteredData.length,
      uniqueRegistrationIds: [...new Set(filteredData.map(d => d.REGISTRATION_ID))],
      uniqueIpAddresses: [...new Set(filteredData.map(d => d.IP_ADDRESS))]
    };

    return { data: filteredData, stats, totalCount: filteredData.length, hasMore: false };
  }

  // Fetch data from backend if not cached
  try {
    const proxyUrl = `${process.env.REACT_APP_BASE_URL}/api/v2/notifications/bulk-signal/proxy`;
    const response = await axios.post(
      proxyUrl,
      { s3Key: fileLocations, limit, offset },
      { headers: { Authorization: `Bearer ${user.token}` } }
    );

    if (response.data.data) {
      rawDataCache.set(cacheKey, response.data.data); // Cache the data
    }

    return {
      data: response.data.data || [],
      stats: response.data.stats,
      totalCount: response.data.totalCount,
      hasMore: response.data.hasMore
    };
  } catch (error) {
    console.error("Error downloading or parsing CSV:", error);
    return { data: [], stats: null, totalCount: 0, hasMore: false };
  }
};

// Download function remains the same
const downloadCSV = async (bulkSignal, user) => {
  try {
    const proxyUrl = `${process.env.REACT_APP_BASE_URL}/api/v2/notifications/bulk-signal/proxy`;
    const response = await axios.post(
      proxyUrl,
      {
        s3Key: bulkSignal.s3Key || bulkSignal.s3Location || bulkSignal.fileLocation,
        download: true // New flag to indicate file download
      },
      {
        headers: {
          Authorization: `Bearer ${user.token}`,
          Accept: "text/csv,text/plain,application/csv",
        },
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bulk_signals_${bulkSignal._id}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV:', error);
  }
};

// Helper function to convert hex to rgb (unchanged)
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [255, 115, 0];
};

export const createScatterplotLayer = (
  data,
  bulkSignalId,
  setSelectedObject,
  registrationIdColors,
  ipAddressColors,
  selectedObject,
  uniqueRegistrationIds,
  uniqueIpAddresses
) => {
  if (!data || data.length === 0) {
    console.warn("No data provided for scatterplot layer");
    return null;
  }

  let displayData = data;
  if (selectedObject?.showOnly === true && selectedObject?.registrationId) {
    displayData = data.filter(d => d.REGISTRATION_ID === selectedObject.registrationId);
    if (displayData.length === 0) {
      return null;
    }
  }

  try {
    return new ScatterplotLayer({
      id: `scatterplot-layer-${bulkSignalId}`,
      data: displayData,
      pickable: true,
      opacity: 0.6,
      stroked: false,
      filled: true,
      radiusScale: 6,
      radiusMinPixels: 6,
      radiusMaxPixels: 6,
      coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
      getPosition: (d) => [parseFloat(d.LONGITUDE), parseFloat(d.LATITUDE)],
      getRadius: 1,
      getFillColor: (d) => {
        if (
          (selectedObject &&
            d.REGISTRATION_ID === selectedObject?.REGISTRATION_ID) ||
          selectedObject?.registrationId === d.REGISTRATION_ID
        ) {
          return [2, 113, 255];
        }
  
        // Check for IP Address colors
        if (ipAddressColors?.length > 0) {
          const colorObj = ipAddressColors.find(i => i.ipAddress === d.IP_ADDRESS);
          return colorObj ? hexToRgb(colorObj.color) : [255, 115, 0];
        }
  
        // Check for Registration ID colors
        if (registrationIdColors?.length > 0) {
          const colorObj = registrationIdColors.find(
            i => i.registrationId === d.REGISTRATION_ID
          );
          return colorObj ? hexToRgb(colorObj.color) : [255, 115, 0];
        }
  
        return [255, 115, 0];
      },
      parameters: {
        depthTest: false,
        blend: true,
        blendFunc: [
          WebGLRenderingContext.ONE,
          WebGLRenderingContext.ONE_MINUS_SRC_ALPHA,
        ],
        blendEquation: WebGLRenderingContext.FUNC_ADD,
        pixelSnapType: "h",
        depthMask: false
      },
      onClick: ({ object }) => {
        console.log("Clicked object:", object);
        setSelectedObject?.(object);
      },
      transitions: { 
        getPosition: 0,
        getFillColor: {
          duration: 0
        }
      },
      updateTriggers: {
        getFillColor: [
          registrationIdColors,
          ipAddressColors,
          uniqueRegistrationIds,
          uniqueIpAddresses,
          selectedObject,
        ],
      },
    });
  } catch (error) {
    console.error("Error creating ScatterplotLayer:", error);
    return null;
  }
};

export const addBulkSignalsToMap = async (
  bulkSignalsArray,
  setSelectedObject,
  user,
  registrationIdColors,
  ipAddressColors,
  selectedObject,
  globalStartDateTime,
  globalEndDateTime
) => {
  if (!bulkSignalsArray || !Array.isArray(bulkSignalsArray)) {
    console.warn("Invalid bulkSignalsArray provided");
    return [];
  }

  const newLayers = [];
  const CHUNK_SIZE = 500000;

  // Check if we're dealing with raw data points or bulk signal metadata
  if (!bulkSignalsArray || !Array.isArray(bulkSignalsArray)) {
    console.warn("Invalid bulkSignalsArray provided");
    return [];
  }

  // If dealing with raw data points, they're already filtered
  const isRawData = bulkSignalsArray.length > 0 && 
    (bulkSignalsArray[0].LATITUDE !== undefined || bulkSignalsArray[0].LONGITUDE !== undefined);

  if (isRawData) {
    // Process the pre-filtered data
    try {
      const groupedData = bulkSignalsArray.reduce((acc, point) => {
        const id = point._id || 'default';
        if (!acc[id]) acc[id] = [];
        acc[id].push(point);
        return acc;
      }, {});

      return Object.entries(groupedData).map(([id, data]) => {
        const layer = createScatterplotLayer(
          data,
          id,
          setSelectedObject,
          registrationIdColors,
          ipAddressColors,
          selectedObject,
          [],
          []
        );

        if (layer) {
          layer.stats = {
            totalPoints: data.length,
            uniqueRegistrationIds: [...new Set(data.map(d => d.REGISTRATION_ID))],
            uniqueIpAddresses: [...new Set(data.map(d => d.IP_ADDRESS))]
          };
          return layer;
        }
        return null;
      }).filter(Boolean);
    } catch (error) {
      console.error("Error processing raw signal data:", error);
      return [];
    }
  } else {
    // Original metadata-based processing
    for (const bulkSignal of bulkSignalsArray) {
      const locations =
        bulkSignal.s3Key || bulkSignal.s3Location || bulkSignal.fileLocation;

      if (!locations || !Array.isArray(locations) || locations.length === 0) {
        console.warn("Invalid locations for bulk signal:", bulkSignal);
        continue;
      }

      try {
        const { data: combinedData, stats } = await downloadAndParseCSV(
          locations, 
          user,
          CHUNK_SIZE,
          0,
          {
            startDateTime: globalStartDateTime,
            endDateTime: globalEndDateTime
          }
        );

        const uniqueRegistrationIds = stats.uniqueRegistrationIds.slice(0, 10);
        const uniqueIpAddresses = stats.uniqueIpAddresses
          .filter(ip => ip !== "NULL")
          .slice(0, 10);

        const layer = createScatterplotLayer(
          combinedData,
          bulkSignal._id,
          setSelectedObject,
          registrationIdColors,
          ipAddressColors,
          selectedObject,
          uniqueRegistrationIds,
          uniqueIpAddresses
        );

        if (layer) {
          layer.stats = stats;
          layer.currentOffset = CHUNK_SIZE;
          layer.hasMore = stats.totalPoints > CHUNK_SIZE;
          newLayers.push(layer);
        }
      } catch (error) {
        console.error("Error processing bulk signal:", error);
      }
    }
  }

  return newLayers;
};

export default addBulkSignalsToMap;